import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableCell, TableSortLabel, TableHead, TableRow, TableBody, useMediaQuery, Theme } from '@mui/material';
import { Order } from 'models/order';
import { OrderItemContext } from 'models/order-item-events';
import { OrderBy } from 'models/search-result';
import ExternalWorkflowOrderItem from './external-workflow-order-item.component';

type ExternalWorkflowOrderTableProps = {
    orders: Order[];
    hidePatientDetails?: boolean;
    onContextMenu: (orderContext: OrderItemContext) => void;
    setOrderOnField?: (filed: string) => void;
    orderBy?: OrderBy;
    batchActionSelected?: boolean;
    onHandleCheckedOrders?: (order: Order, checked: boolean, orderIndex: number) => void;
    isAtLimit?: boolean;
    checkedOrderIds?: Set<string>;
};
const ExternalWorkflowOrderTable: FC<ExternalWorkflowOrderTableProps> = ({
    orders,
    onContextMenu,
    hidePatientDetails,
    setOrderOnField,
    orderBy,
    batchActionSelected,
    onHandleCheckedOrders,
    isAtLimit,
    checkedOrderIds,
}: ExternalWorkflowOrderTableProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

    const sortOrder = (field: string) => {
        if (setOrderOnField) {
            setOrderOnField(field);
        }
    };

    return (
        <Table size={!isLargeScreen ? 'small' : 'medium'}>
            <TableHead>
                <TableRow>
                    <TableCell></TableCell>
                    <TableCell>{translate('orders:ORDER_TAG')}</TableCell>
                    <TableCell>
                        <TableSortLabel
                            onClick={() => sortOrder('workFlow.currentStep.dateTime')}
                            active={orderBy?.orderBy === 'workFlow.currentStep.dateTime'}
                            direction={orderBy?.orderDirection}
                        >
                            {translate('workflow:TIME_IN_STEP')}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>{translate('common:ORDER_ID')}</TableCell>
                    {!hidePatientDetails ? <TableCell>{translate('common:PATIENT')}</TableCell> : undefined}
                    <TableCell>{translate('orders:CONSTRUCTIONS')}</TableCell>
                    <TableCell>{translate('orders:MATERIALS')}</TableCell>
                    <TableCell>
                        <TableSortLabel
                            onClick={() => sortOrder('linkedExtTechs.dateAdded')}
                            active={orderBy?.orderBy === 'linkedExtTechs.dateAdded'}
                            direction={orderBy?.orderDirection}
                        >
                            {translate('orders:ASSIGNED')}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                        <TableSortLabel
                            onClick={() => sortOrder('deliveryDetails.date')}
                            active={orderBy?.orderBy === 'deliveryDetails.date'}
                            direction={orderBy?.orderDirection}
                        >
                            {translate('common:DELIVERY')}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                        <TableSortLabel
                            onClick={() => sortOrder('deliveryDetails.externalDeliveryDate')}
                            active={orderBy?.orderBy === 'deliveryDetails.externalDeliveryDate'}
                            direction={orderBy?.orderDirection}
                        >
                            {translate('orders:EXTERNAL_DELIVERY_DATE')}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell></TableCell>
                </TableRow>
            </TableHead>
            {orders ? (
                <TableBody>
                    {orders.map((order, orderIndex) => (
                        <ExternalWorkflowOrderItem
                            key={order._id}
                            order={order}
                            hidePatientDetails={hidePatientDetails}
                            onOrderContextMenu={onContextMenu}
                            batchActionSelected={batchActionSelected}
                            onHandleCheckedOrders={onHandleCheckedOrders}
                            orderIndex={orderIndex}
                            isAtLimit={isAtLimit}
                            isChecked={checkedOrderIds?.has(order._id as string) ?? false}
                        />
                    ))}
                </TableBody>
            ) : undefined}
        </Table>
    );
};

export default ExternalWorkflowOrderTable;
